import jwtDecode, { JwtPayload } from "jwt-decode";
import AuthService from "./AuthService";

/** Fragment parameter carrying a bearer token handed over by another Bits site. */
const TOKEN_PARAM = "bits_token";

/**
 * Session state that belongs to whoever was signed in before. A hand-off can switch
 * users, and a Saleor JWT / store user minted for the previous one would then be sent
 * on this one's checkout calls. Cart state is already keyed per user, so it's left alone.
 */
const PREVIOUS_SESSION_KEYS = ["checkoutStoreToken", "checkoutStoreUser"];

/**
 * A token that arrived without a trusted referrer, held back until the user confirms whose
 * account it is. Module state rather than storage: it must not survive the page.
 */
let pendingToken: string | null = null;

function isUsable(token: string): boolean {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    return Date.now() < (exp ?? 0) * 1000;
  } catch {
    return false;
  }
}

/**
 * The Bits sites allowed to hand a session over, with and without the www.
 *
 * `||` rather than `??`: an env var declared but left blank reaches the bundle as "", which
 * is not nullish. `??` would keep the empty string, `new URL("")` would throw, and every
 * hand-off would silently fall through to the confirmation screen.
 */
function trustedOrigins(): string[] {
  try {
    const { protocol, host } = new URL(process.env.REACT_APP_WEB_LANDING_URL || "https://www.getbits.app");
    const bare = host.replace(/^www\./, "");
    return [`${protocol}//${bare}`, `${protocol}//www.${bare}`];
  } catch {
    return [];
  }
}

/**
 * Whether this navigation came from a Bits site. A page can't forge the referrer of a
 * navigation it initiates, so this distinguishes our own "Store" button from a link pasted
 * into an email, a chat or a third-party page — the ways an attacker would actually deliver
 * one. It fails closed: no referrer means no automatic sign-in.
 */
function cameFromTrustedOrigin(): boolean {
  if (!document.referrer) {
    return false;
  }
  try {
    return trustedOrigins().includes(new URL(document.referrer).origin);
  } catch {
    return false;
  }
}

function adopt(token: string): void {
  if (token !== AuthService.getAuthToken()) {
    PREVIOUS_SESSION_KEYS.forEach((key) => window.sessionStorage.removeItem(key));
  }
  AuthService.saveAuthToken(token);
}

/**
 * Signs the user in from a token passed in the URL fragment, e.g.
 * `https://uk.getbits.app/store#bits_token=<jwt>`.
 *
 * Login and sign-up now live on the marketing site (www.getbits.app). That's a different
 * origin, so it can't write this app's localStorage — it hands the bearer token over in the
 * fragment instead. Fragments are never sent to a server, which keeps the token out of
 * access logs, the CDN and the `Referer` header; it's stripped from the URL below so it
 * doesn't linger in the address bar or in history either.
 *
 * Called from index.tsx before the first render, so the token is in place by the time
 * <RequireAuth> looks for it. An effect would run after that check and lose the race to
 * the /login redirect.
 *
 * ## Why the referrer gate
 *
 * A token in a link is a session-fixation risk: anyone can put *their own* valid token in
 * the fragment and send it to you, and you'd silently end up browsing as them. The damage
 * runs backwards from the obvious — orders bill the account that owns the token, so there's
 * nothing to steal that way. What an attacker gets is the shipping address and card details
 * you then type into what you believe is your own checkout.
 *
 * So a token is adopted outright only when the navigation came from a Bits origin, which an
 * attacker's page cannot fake. Anything else is held in `pendingToken` for <HandoffGate> to
 * confirm against the account's own email — the point being that the victim of such a link
 * is shown, in plain words, that they're about to sign in as somebody else.
 *
 * This is a mitigation, not the real fix. Properly, the token would never travel in a URL:
 * the API would mint a single-use, short-lived hand-off code, and the store would redeem it
 * for a token of its own. That needs an endpoint that doesn't exist yet.
 *
 * A token that's expired or unreadable is discarded rather than saved: the user then falls
 * through to the normal /login redirect instead of a session that 401s on every call. Note
 * that decoding proves nothing about authenticity — only the API can do that, and it will
 * reject a forged token on the first call. The check is only here to avoid storing a token
 * that's already dead.
 */
export function consumeHandoffToken(): void {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash.includes(TOKEN_PARAM)) {
    return;
  }

  const params = new URLSearchParams(hash);
  const token = params.get(TOKEN_PARAM);
  params.delete(TOKEN_PARAM);

  const remaining = params.toString();
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}${remaining ? `#${remaining}` : ""}`
  );

  if (!token || !isUsable(token)) {
    return;
  }
  if (cameFromTrustedOrigin()) {
    adopt(token);
    return;
  }
  pendingToken = token;
}

/** The token awaiting confirmation, if any. Safe to call repeatedly — it doesn't consume. */
export function getPendingHandoffToken(): string | null {
  return pendingToken;
}

/** The user confirmed the account is theirs. */
export function acceptPendingHandoff(): void {
  if (pendingToken) {
    adopt(pendingToken);
    pendingToken = null;
  }
}

/** The user declined, or the token turned out to be unusable. */
export function rejectPendingHandoff(): void {
  pendingToken = null;
}
